import type { PhotoPresentationLayout } from './photoPresentationLayout';

/**
 * The four full-bleed header layouts that render via `StandaloneWebsiteHeader` — a hero sized to
 * the viewport (`100svh`, or `90svh` on mobile under the bouncing-chevron flag) that sits *above*
 * the page frame rather than in flow inside it. Every other layout renders in-flow via HeaderV2.
 *
 * The invariant callers rely on: when one of these is active, the frame below it already starts at
 * or past the fold. Mirrors `isMastheadCompatibleLayout` in `./mastheadCompat`, which allowlists
 * the in-flow layouts.
 *
 * The two sets are intended to partition the layout space — disjoint, and together exhaustive over
 * `PHOTO_PRESENTATION_CONFIG`. `standaloneHeroLayout.spec.ts` asserts both properties. Note the
 * predicates are typed over different aliases (`PhotoPresentationLayout` here,
 * `SupportedBrannanHeaderPhotoLayout` there); the partition only means anything while those two
 * describe the same set of layouts, so if they ever diverge, revisit the spec rather than trusting
 * it to keep failing.
 */
export const STANDALONE_HERO_LAYOUTS: ReadonlySet<PhotoPresentationLayout> = new Set<PhotoPresentationLayout>([
  'photoBackground',
  'photoBackgroundMonogram',
  'videoBackground',
  'videoBackgroundMonogram',
]);

/**
 * True when the event's header renders as a standalone viewport-height hero above the page frame.
 *
 * Callers use this to answer "does something above the frame already push it past the fold?" — see
 * the CLS floor in `LayoutBrannan`. Note this describes the *layout choice*, not a measured height:
 * admin preview contexts (template-preview modal, compact video view) can render these heroes
 * shorter than a viewport, so treat it as a guest-site invariant rather than a universal one.
 */
export const isStandaloneHeroLayout = (layout: Maybe<PhotoPresentationLayout>): boolean => {
  return !!layout && STANDALONE_HERO_LAYOUTS.has(layout);
};
